import React, { useState } from 'react';
import { Grid, TextField, Typography, Box, Button, Card, CardContent, Select, MenuItem, FormControl, InputLabel, Checkbox, FormControlLabel, useTheme } from '@mui/material';

import { generateTenantToken, makeEncryptedApiCall } from "./utils/apiUtils";

function BankBancassuranceForm({ setScore, onComputed, setErrorMessage, setOpenSnackbar, setIsLoading, setFixedPay, setVariablePay }) {
  const theme = useTheme();
  const [tenant, setTenant] = useState({ tenantId: '1', tenantName: '' });
  const [form, setForm] = useState({
    premium: '30,000',
    income: '5,00,000',
    sumInsured: '2,00,000',
    policyType: 'Term Insurance',
    age: '27',
    familyStatus: 'Married',
    paymentMode: 'Auto-debit',
    existingInvestments: false,
    gender: "Male",
    state: "Maharashtra",
    preexisting_conditions: ["Coronary artery disease"],
    nominee_relation: "Spouse",
    credit_score: "750",
    occupation: "Salaried",
    education_level: "Graduate",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleTenant = (e) => setTenant({ ...tenant, [e.target.name]: e.target.value });
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const params = [
    { name: 'Credit Reliability', score: 85, weight: 0.28 },
    { name: 'Customer Spending', score: 22, weight: 0.23 },
    { name: 'Payment History Score', score: 92, weight: 0.21 },
    { name: 'Cross-Sell Coverage Depth', score: 45, weight: 0.11 },
    { name: 'Coverage Adequacy Score', score: 55, weight: 0.09 },
    { name: 'Risk Affinity', score: 87, weight: 0.07 },
    { name: 'Claim Severity', score: 93, weight: 0.06 },
    { name: 'Referral and Advocacy Potential', score: 75, weight: 0.05 }
  ];

  const formula = '(95*0.28) + (92*0.23) + (88*0.21) + (85*0.11) + (100*0.09) + (80*0.07) + (90*0.06) + (75*0.05) = 99.34';

  const handleSubmit = async () => {
    if (!tenant.tenantId.trim() || !tenant.tenantName.trim()) {
      setErrorMessage("Please enter both Tenant ID and Tenant Name.");
      setOpenSnackbar(true);
      setIsSubmitting(false);
      setIsLoading(false);
      return;
    }

    try {
      // Step 1: Generate tenant token
      const tokenResponse = await generateTenantToken(tenant.tenantId, tenant.tenantName);
      
      // Step 2: Make encrypted API call
      const url = `/api/v1.0/tenants/${tenant.tenantId}/score/bank/bancassurance`;
      const score = await makeEncryptedApiCall(url, form, tokenResponse.token);
      
      setScore(score.output);
      const weights = params.reduce((obj, p) => { obj[p.name.replace(/\s+/g, '_').toLowerCase()] = p.weight; return obj; }, {});
    // setScore(scores);
      onComputed && onComputed({ weights, finalScore: 99, finalPay: 8976.25, formula });
      // setIsSubmitting(false);
      setFixedPay(8000);
      setVariablePay(2000);
    } catch (error) {
      console.error("Error fetching bank score:", error);
      let detailedMessage = "An error occurred while fetching the bank score.";
      if (error.response && error.response.data && error.response.data.error) {
        detailedMessage = error.response.data.error;
      } else if (error.message) {
        detailedMessage = error.message;
      }
      setErrorMessage(detailedMessage);
      setOpenSnackbar(true);
    } finally {
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card elevation={0} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>Tenant Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField name="tenantId" value={tenant.tenantId} onChange={handleTenant} fullWidth size="small" placeholder="Tenant ID" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField name="tenantName" value={tenant.tenantName} onChange={handleTenant} fullWidth size="small" placeholder="Tenant Name" autoComplete="off" />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {[
        // { name: 'premium', label: 'Annual Premium' },
        // { name: 'income', label: 'Customer Income' },
        { name: 'policyType', label: 'Policy Type' },
        { name: 'sumInsured', label: 'Policy Sum Insured' },
        { name: 'age', label: 'Customer Age' },
        { name: 'gender', label: 'Customer Gender' },
        { name: 'state', label: 'Customer State' },
        { name: 'preexisting_conditions', label: 'Customer Preexisting Conditions' },
        { name: 'nominee_relation', label: 'Customer Nominee Relation' },
        { name: 'credit_score', label: 'Customer Credit Score' },
        { name: 'occupation', label: 'Customer Occupation' },
        { name: 'education_level', label: 'Customer Education Level' }
      ].map((f) => (
        <Grid item xs={12} sm={6} key={f.name}>
          <Box sx={{ mb: 1 }}>
            <Typography variant="subtitle1" fontWeight={500}>{f.label}</Typography>
          </Box>
          <TextField name={f.name} value={form[f.name]} onChange={handleChange} fullWidth size="small" placeholder={`Enter ${f.label}`} />
        </Grid>
      ))}

      <Grid item xs={12} sm={6}>
        <FormControl fullWidth size="small">
          <InputLabel id="family-label">Family Status</InputLabel>
          <Select labelId="family-label" label="Family Status" name="familyStatus" value={form.familyStatus} onChange={handleChange}>
            <MenuItem value="Single">Single</MenuItem>
            <MenuItem value="Married">Married</MenuItem>
            <MenuItem value="Married with Children">Married with Children</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth size="small">
          <InputLabel id="payment-label">Payment Mode</InputLabel>
          <Select labelId="payment-label" label="Payment Mode" name="paymentMode" value={form.paymentMode} onChange={handleChange}>
            <MenuItem value="Auto-debit">Auto-debit</MenuItem>
            <MenuItem value="Manual">Manual</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel control={<Checkbox checked={form.existingInvestments} onChange={handleChange} name="existingInvestments" />} label="Existing Investments?" />
      </Grid>

      <Grid item xs={12}>
      <Button 
        variant="contained" 
        fullWidth 
        size="large"
        onClick={handleSubmit} 
        disabled={isSubmitting} 
        sx={{
          mt: 2, py: 1.5, borderRadius: 2, textTransform: 'none', fontSize: '1rem', fontWeight: 600,
          background: 'linear-gradient(135deg,rgb(88, 139, 249) 0%, #2563EB 100%)',
          boxShadow: '0 10px 20px rgba(37, 99, 235, 0.2)',
          '&:hover': { boxShadow: '0 15px 30px rgba(37, 99, 235, 0.3)', transform: 'translateY(-2px)' },
          transition: 'all 0.3s ease',
          '&.Mui-disabled': {
              background: theme.palette.grey[300],
              boxShadow: 'none',
              color: theme.palette.grey[500],
              cursor: 'not-allowed'
          }
        }}

        >
          {isSubmitting ? 'Calculating...' : 'Calculate Bancassurance Score'}
        </Button>
      </Grid>
    </Grid>
  );
}

export default BankBancassuranceForm;


